import { useApolloClient } from "@apollo/client";
import { Button } from "@chakra-ui/react";
import React, { useCallback } from "react";
import { RiArchiveLine } from "react-icons/ri";
import { archiveTeamMembers } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { Selection } from "src/hooks/useMultiselectState";
import * as GQL from "src/types/graphql";
import { GET_MEMBERS } from "./graphql/queries";

type Props = {
  active: boolean;
  selection: Selection<string, GQL.GetMembers_person>;
  clearSelection: () => void;
  onFetchAll: () => Promise<GQL.GetMembers_person[]>;
  refetch: () => Promise<unknown>;
};

export const BulkArchiveTeamMembersButton: React.FC<Props> = ({
  active,
  selection,
  clearSelection,
  onFetchAll,
  refetch,
}) => {
  const archiveDialog = useConfirmationDialog(
    active
      ? {
          header: "Archive members",
          body: "Deleting team members will remove all access to the organization for them. This can't be undone nor recovered.",
          confirmButton: {
            label: "Yes, archive",
          },
        }
      : {
          header: `Unarchive members`,
          body: "Unarchiving team members will grant all access to the organization for them. ",
          confirmButton: {
            label: "Yes, unarchive",
          },
        }
  );
  const apolloClient = useApolloClient();
  const toast = useAvelaToast();
  const archiveMembersHandler = useCallback(async () => {
    if (
      await archiveDialog.confirm({
        onConfirm: (helpers) => {
          helpers.setButton({
            label: "",
            isLoading: true,
            loadingText: active ? "Archiving..." : "Unarchiving...",
          });
        },
      })
    ) {
      try {
        const members = await selection.materialize(onFetchAll);
        const personIds = members.map((member) => member.id);
        await archiveTeamMembers({
          body: personIds.map((personId) => ({
            personId,
            active: !active,
          })),
        });
        clearSelection();
        // We need both refetch and cache eviction here to avoid stale data on the selection after update
        await apolloClient.refetchQueries({
          include: [GET_MEMBERS],
          updateCache: (cache) => {
            cache.evict({ fieldName: "person" });
            cache.gc();
          },
        });
      } catch (error) {
        console.error(error);
        toast.genericError();
      }

      archiveDialog.helpers.close();
    }
  }, [
    active,
    apolloClient,
    archiveDialog,
    clearSelection,
    onFetchAll,
    selection,
    toast,
  ]);

  return (
    <>
      <Button
        variant="ghost"
        leftIcon={<RiArchiveLine />}
        colorScheme="gray"
        size="sm"
        onClick={archiveMembersHandler}
      >
        {active ? "Archive members" : "Unarchive members"}
      </Button>
      {archiveDialog.confirmationDialog}
    </>
  );
};
